import { Component, computed, input, OnInit, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AutocompleteComponent } from '../../../../common/autocomplete/autocomplete.component';
import { BaseComponent } from '../../../../common/base.component';
import { SelectGearTierComponent } from '../../../../common/select/select-gear-tier/select-gear-tier.component';
import { SelectComponent, SelectOptionComponent } from '../../../../common/select/select.component';
import { DefenceType, ArmourType, GearBaseTypeMode, Rarity } from '../../../../models/enums';
import { ArmourFilter, AdvancedEquipmentConditions, GearHighlightConfig } from '../../../../models/filter-models';
import { BaseType } from '../../../../models/item-data-models';
import { FilterService } from '../../../../services/filter.service';
import { ItemDataService } from '../../../../services/item-data.service';
import { GenerationHelperService } from '../../../../services/generators/generation-helper.service';
import { AdvancedEquipmentConditionsComponent } from '../../advanced-equipment-conditions/advanced-equipment-conditions.component';
import { MyEquipmentHighlightsComponent } from '../../my-equipment-highlights/my-equipment-highlights.component';
import { SelectRarityComponent } from '../../../../common/select/select-rarity/select-rarity.component';
import { TooltipWrapperComponent } from '../../../../common/tooltip-wrapper/tooltip-wrapper.component';

@Component({
  selector: 'app-armour-filter',
  imports: [FormsModule, AutocompleteComponent, SelectGearTierComponent, SelectComponent, SelectOptionComponent,
    SelectRarityComponent, AdvancedEquipmentConditionsComponent, MyEquipmentHighlightsComponent, TooltipWrapperComponent],
  templateUrl: './armour-filter.component.html',
  styleUrl: './armour-filter.component.scss'
})
export class ArmourFilterComponent extends BaseComponent implements OnInit {
  armour = input.required<ArmourFilter>();
  index = input.required<number>();
  remove = output<number>();

  GearBaseTypeMode = GearBaseTypeMode;

  hasAdvancedConditions = 0;
  advancedConditionsOpen = false;
  hasHighlightConfig = 0;
  highlightConfigOpen = false;

  armourTypes: BaseType[] = [];
  defenceTypes = [
    { name: 'Arm', image: '/img/shields/shield-red.png' },
    { name: 'Eva', image: '/img/shields/shield-green.png' },
    { name: 'ES', image: '/img/shields/shield-blue.png' },
    { name: 'Arm/Eva', image: '/img/shields/shield-red-green.png' },
    { name: 'Arm/ES', image: '/img/shields/shield-red-blue.png' },
    { name: 'Eva/ES', image: '/img/shields/shield-green-blue.png' }
  ];

  specificBaseTypeAutocompleteOptions = computed(() => {
    const a = this.armour();
    return this.generationHelper.getMyGearBaseTypeAutocompleteOptions(undefined, a.armourType, a.defenceType);
  });

  constructor(private filterService: FilterService, private itemDataService: ItemDataService, private generationHelper: GenerationHelperService) {
    super();
    this.armourTypes = [
      { name: 'Body Armours', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Body Armours')!.baseTypes[1].image! },
      { name: 'Helmets', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Helmets')!.baseTypes[6].image! },
      { name: 'Gloves', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Gloves')!.baseTypes[1].image! },
      { name: 'Boots', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Boots')!.baseTypes[1].image! },
      { name: 'Shields', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Shields')!.baseTypes[0].image! },
    ]

    if (this.poe2) {
      this.armourTypes.push({ name: 'Bucklers', image: this.itemDataService.getItemData().find(i => i.itemClass === 'Bucklers')!.baseTypes[0].image! });
    }
  }

  ngOnInit(): void {
    this.hasAdvancedConditions = this.getNumberOfAdvancedConditions();
    this.hasHighlightConfig = this.getNumberOfHighlightConfig();
  }

  toggleAdvancedOptionsOpen() {
    this.advancedConditionsOpen = !this.advancedConditionsOpen;
  }

  toggleHighlightConfigOpen() {
    this.highlightConfigOpen = !this.highlightConfigOpen;
  }

  getNumberOfAdvancedConditions() {
    return AdvancedEquipmentConditions.hasChanges(this.armour());
  }

  getNumberOfHighlightConfig() {
    return GearHighlightConfig.hasChanges(this.armour().highlightConfig);
  }

  toggleShowArmour() {
    this.armour().show = !this.armour().show;
    if (!this.armour().show) {
      this.advancedConditionsOpen = false;
      this.highlightConfigOpen = false;
    }
    this.updateFilter();
  }

  removeArmourType() {
    this.remove.emit(this.index());
  }

  defenceTypeChanged(defenceTypes: string[]) {
    this.armour().defenceType = defenceTypes.map(d => d as DefenceType);
    if (this.armour().baseTypeMode === GearBaseTypeMode.Specific) {
      this.pruneArmourSpecificBaseTypes();
    }
    this.updateFilter();
  }

  armourTypeChanged(armourTypes: string[]) {
    this.armour().armourType = armourTypes.map(a => a as ArmourType);
    if (this.armour().baseTypeMode === GearBaseTypeMode.Specific) {
      this.pruneArmourSpecificBaseTypes();
    }
    this.updateFilter();
  }

  toggleArmourBaseTypeMode(): void {
    if (this.armour().baseTypeMode === GearBaseTypeMode.Tier) {
      this.armour().baseTypeMode = GearBaseTypeMode.Specific;
      if (this.armour().specificBaseTypes.length === 0) {
        this.armour().specificBaseTypes = ['All'];
      }
    } else {
      this.armour().baseTypeMode = GearBaseTypeMode.Tier;
    }
    this.updateFilter();
  }

  specificArmourBaseTypesChanged(values: string[]): void {
    this.armour().specificBaseTypes = values;
    this.updateFilter();
  }

  private pruneArmourSpecificBaseTypes(): void {
    const armour = this.armour();
    if (armour.specificBaseTypes.includes('All')) {
      return;
    }
    const eligible = new Set(this.generationHelper.resolveGearRuleEligibleBaseTypeNames(undefined, armour.armourType, armour.defenceType));
    const next = armour.specificBaseTypes.filter(b => eligible.has(b));
    armour.specificBaseTypes = next.length > 0 ? next : ['All'];
  }

  minUnidentifiedTierChanged(unidentifiedTier: number | null) {
    const armour = this.armour();
    if (armour.rarity === Rarity.Normal && unidentifiedTier) {
      armour.rarity = Rarity.Magic;
    }

    if (armour.highlightConfig?.rareMinTier && unidentifiedTier) {
      armour.highlightConfig.rareMinTier = unidentifiedTier;
    }
    if (armour.highlightConfig?.magicMinTier && unidentifiedTier) {
      armour.highlightConfig.magicMinTier = unidentifiedTier;
    }
    if (armour.highlightConfig?.lowTierRareMinTier && unidentifiedTier) {
      armour.highlightConfig.lowTierRareMinTier = unidentifiedTier;
    }
    if (armour.highlightConfig?.lowTierMagicMinTier && unidentifiedTier) {
      armour.highlightConfig.lowTierMagicMinTier = unidentifiedTier;
    }

    this.updateFilter();
  }

  minRarityChanged(rarity: Rarity) {
    const armour = this.armour();
    if (rarity === Rarity.Normal) {
      armour.unidentifiedTier = undefined;
      armour.highlightConfig.regularNormal = true;
      armour.highlightConfig.regularMagic = true;
      armour.highlightConfig.magic = true;
      armour.highlightConfig.lowTierMagic = true;
    }

    if (armour.rarity === Rarity.Magic) {
      armour.highlightConfig.regularNormal = false;
      armour.highlightConfig.regularMagic = true;
      armour.highlightConfig.magic = true;
      armour.highlightConfig.lowTierMagic = true;
    }

    if (armour.rarity === Rarity.Rare) {
      armour.highlightConfig.regularNormal = false;
      armour.highlightConfig.regularMagic = false;
      armour.highlightConfig.magic = false;
      armour.highlightConfig.lowTierMagic = false;
    }

    this.hasHighlightConfig = this.getNumberOfHighlightConfig();
    this.updateFilter();
  }

  updateFilter() {
    this.filterService.updateFilter();
  }
}
